/**
 * Error boundary component
 * Catches JavaScript errors and displays fallback UI
 */

import { Component, ReactNode, ErrorInfo } from 'react';
import { Result, Button, Typography, Collapse } from 'antd';
import { WarningOutlined, ReloadOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Log error to console
    console.error('Error caught by boundary:', error, errorInfo);

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, showDetails = false } = this.props;

    if (hasError) {
      // Render custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Render default error UI
      return (
        <div
          style={{
            padding: 48,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}
        >
          <Result
            status="error"
            title="Something went wrong"
            subTitle="An unexpected error occurred. Please try refreshing the page."
            extra={[
              <Button key="reset" onClick={this.handleReset}>
                Try Again
              </Button>,
              <Button
                key="reload"
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleReload}
              >
                Refresh Page
              </Button>,
            ]}
          >
            {showDetails && error && (
              <Collapse
                items={[
                  {
                    key: 'details',
                    label: 'Error Details',
                    children: (
                      <div style={{ textAlign: 'left' }}>
                        <Paragraph>
                          <Text strong>Error: </Text>
                          <Text code>{error.message}</Text>
                        </Paragraph>
                        {errorInfo && (
                          <Paragraph>
                            <Text strong>Component Stack:</Text>
                            <pre
                              style={{
                                fontSize: 12,
                                overflow: 'auto',
                                maxHeight: 200,
                                padding: 8,
                                backgroundColor: 'var(--color-bg-spotlight)',
                                borderRadius: 4,
                              }}
                            >
                              {errorInfo.componentStack}
                            </pre>
                          </Paragraph>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </Result>
        </div>
      );
    }

    return children;
  }
}

/**
 * Error fallback component for simpler error displays
 */
export function ErrorFallback({
  error,
  resetError,
}: {
  error?: Error;
  resetError?: () => void;
}) {
  return (
    <div
      style={{
        padding: 24,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <WarningOutlined style={{ fontSize: 48, color: 'var(--color-error)' }} />
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Something went wrong
        </Text>
        {error && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {error.message}
          </Text>
        )}
      </div>
      {resetError && (
        <Button onClick={resetError} size="small">
          Try Again
        </Button>
      )}
    </div>
  );
}

/**
 * Hook-based error boundary wrapper (for functional components)
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WrappedComponent(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
